"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Quote from "@/components/Icons/Quote";
import { InlineVideo } from "@/components/components/InlineVideo";
import { Button } from "@/components/actions/Button";
import Link from "next/link";

type TestimonialVariant = {
  id: "wizard" | "snoopstein";
  personaLabel: string;
  videoSrc: string;
  quote: string;
};

function randomIndex(max: number) {
  return Math.floor(Math.random() * max);
}

export function RotatingTestimonialsSection({ isLoggedIn }: { isLoggedIn: boolean }) {
  const variants: TestimonialVariant[] = useMemo(
    () => [
      {
        id: "wizard",
        personaLabel: "AI wizard",
        videoSrc: "/wizard-quotes-video.mp4",
        quote:
          "Thinknado, solving big, complex problems requires us to move beyond linear thinking and instead embrace uncertainty, imagination, and creative disruption. Just like tornadoes, which spin unpredictably, ideas too need space to move freely, grab inspiration from unlikely places, and evolve without rigid expectations.",
      },
      {
        id: "snoopstein",
        personaLabel: "AI Snoopstein",
        videoSrc: "/Snoopstein-Testimonial.mp4",
        quote:
          "I’ve realized that doing something “wrong” in the course of meaningful exploration is often better than doing something safe and repetitive.",
      },
      {
        id: "llama",
        personaLabel: "AI Llama",
        videoSrc: "/llama-testimonial.mp4",
        quote:
          "The mindset of “try something” is exactly what our industry needs to break out of outdated norms and reimagine what responsible design can look like.",
      }
    ],
    []
  );

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(randomIndex(variants.length));
  }, [variants.length]);

  const pickNextIndex = useCallback(
    (current: number) => {
      if (variants.length <= 1) return current;
      if (variants.length === 2) return current === 0 ? 1 : 0;

      let next = current;
      while (next === current) {
        next = randomIndex(variants.length);
      }
      return next;
    },
    [variants.length]
  );

  const active = variants[activeIndex];

  return (
    <section className=" bg-[#000000] text-white py-24 px-8" style={{
          // paddingTop: `${SITE_HEADER_OFFSET_PX}px`,
          backgroundImage:
            "linear-gradient(to right, rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}>
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-8 md:gap-12 max-w-[1800px] mx-auto items-center">
        {/* <div className="lg:col-span-2 max-w-full overflow-clip">
          <InlineVideo
            key={active.id}
            src={active.videoSrc}
            className="w-full object-cover scale-105"            
            hasSound={true}
            loop={false}
            onEnded={() => setActiveIndex((current) => pickNextIndex(current))}
          />
        </div> */}
        <h2 className="type-4 font-black uppercase text-balance lg:col-span-2">
            WHAT REAL PEOPLE ARE SAYING
          </h2>
        <div className="stack gap-12 lg:col-span-4">          
          <div className="text-2xl md:text-3xl lg:text-5xl italic font-normal leading-[1.2] type-display mt-4 md:mt-6 lg:mt-8 pr-6 md:pr-8 lg:pr-12">
            <Quote color="white" className="w-[60px] md:w-[60px] lg:w-[60px] shrink-0 inline-block mr-4 md:mr-6 lg:mr-8 align-baseline" />
            <span>{active.quote}</span>
            <Quote
              color="white"
              flip
              className="w-[60px] md:w-[60px] lg:w-[60px] shrink-0 inline-block ml-4 md:ml-6 lg:ml-8 align-top mt-2"
            />
          </div>
          <Link
              href={isLoggedIn ? "/app/session-v2" : "/auth/sign-in"}
              className="w-max max-w-full text-center border border-white bg-black text-white px-6 py-3 text-sm font-black uppercase tracking-[0.14em] hover:bg-white hover:text-black transition"
            >
              Try It Yourself
            </Link>
        </div>
      </div>
    </section>
  );
}
